import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Register from './components/Register/Register.jsx'
import Login from './components/Login/Login.jsx';
import ForgotPassword from './components/Login/ForgotPassword.jsx';
import Dashboard from './components/Dashboard/Dashboard.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import VisibeenLandingPage from './components/LandingPage/VisibeenLandingPage.jsx';
import ProfilePage from './components/Dashboard/pages/Profile.jsx';
import BusinessProfile from './components/Dashboard/pages/BusinessProfile.jsx';
import ResetPassword from './components/Login/ResetPassword.jsx';
import EDMsFlow from './components/edms/EDMsFlow.jsx';
import AccountNotFound from './components/gmb_accounts/AccountNotFound.jsx';
import GoogleConnect from './components/Services/GoogleConnect.jsx';
import ContactUs from './components/gmb_accounts/ContactUs.jsx';
import CreateAccount from './components/gmb_accounts/Create_Account/CreateAccount.jsx';
import CreatePostPage from './components/CreatePost/CreatePostPage.jsx';
import Reputation from './components/Reputation/Reputation.jsx';
import ReferEarn from './components/Refer&Earn.jsx';
import MyAccount from './components/MyAccount.jsx';
import FontStyleSelection from './components/FontStyleSelection/FontStyleSelection.jsx';
import PostGenerationResults from './components/PostGenerationResults/PostGenerationResults.jsx';
import FreeWebsite from './components/FreeWebsite/FreeWebsite.jsx';
import GSTInfoForm from './GstAccounts/GSTInfoForm.jsx';
import CROInfoForm from './GstAccounts/CROInfoForm.jsx';
import AccountInfoForm from './GstAccounts/AccountInfoForm.jsx';
import DashboardLayout from './components/Layouts/DashboardLayout.jsx';
import GMBDataFetcher from './components/gmb_accounts/GMBDataFetcher.jsx';
import ProfileStrengthAnalysis from './components/ProfileStrengthAnalysis/ProfileStrengthAnalysis.jsx';
import ProfileStrengthResults from './components/ProfileStrengthResults/ProfileStrengthResults.jsx';
import ReviewsManagement from './components/ReviewsManagement/ReviewsManagement.jsx';
import SetAutoReply from './components/SetAutoReply/SetAutoReply.jsx';
import HeatmapResultsPage from './components/HeatmapResults/HeatmapResultsPage.jsx';
import ProfileStrengthPage from './components/ProfileStrength/ProfileStrengthPage.jsx';
import Performance from './components/Performance/Performance.jsx';
import { getSession } from './utils/authUtils.js';
import AutoTokenManager from './utils/autoTokenUtils.js';
import { AppProvider } from './contexts/AppContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { DefaultProfileProvider } from './contexts/DefaultProfileContext';
import { AccessGateProvider } from './contexts/AccessGateContext';
import { ProfilePlanProvider } from './contexts/ProfilePlanContext';
import UpgradePromptModal from './components/shared/UpgradePromptModal';
import { AdminDataProvider } from './contexts/AdminDataContext';
import { AdminRoleProvider } from './contexts/AdminRoleContext';
import 'leaflet/dist/leaflet.css';
import HeatmapPage from './components/Heatmap/HeatmapPage';
import Holidays from './components/edms/Holidays';
import YourData from './components/YourData/YourData.jsx';
import AdminDashboard from './components/AdminDashboard/AdminDashboard.jsx';
import NewUserOnboarding from './components/Onboarding/NewUserOnboarding.jsx';
import ClientOnboardingGuide from './components/Onboarding/ClientOnboardingGuide.jsx';
import TaskManagementPage from './components/TaskManagement/TaskManagementPage.jsx';
import PlansPricing from './components/Pricing/PlansPricing.jsx';
import TrialRedeemRedirect from './components/TrialRedeemRedirect';
import TrialLanding from './pages/TrialLanding.jsx';
import AboutUsPage from './components/AboutUs/AboutUsPage.jsx';
import PrivacyPolicyPage from './components/PrivacyPolicy/PrivacyPolicyPage.jsx';
import DisclaimerPage from './components/Disclaimer/DisclaimerPage.jsx';
import TermsOfService from './components/TermsOfService/TermsOfService.jsx';
import SessionExpiredHandler from './components/SessionExpiredModal/SessionExpiredHandler.jsx';
import TestSessionExpired from './components/SessionExpiredModal/TestSessionExpired.jsx';
import LiveDemo from './components/FreeWebsite/LiveDemo.jsx';
import VisitRedirect from './components/Directory/VisitRedirect.jsx';
import BusinessView from './components/Directory/BusinessView.jsx';
import OAuthCallback from './components/Auth/OAuthCallback.jsx';
import VisionPostGeneratorPage from './components/VisionPostGenerator/VisionPostGeneratorPage.jsx';
import AdsPage from './pages/AdsPage.jsx';
import MonthlyPlan from './components/MonthlyPlan/MonthlyPlan.jsx';
import TutorialWrapper from './components/Tutorial/TutorialWrapper.jsx';
import RequestsPage from './pages/RequestsPage.jsx';

import logger from 'utils/logger';

import './styles.css';

function App() {
  useEffect(() => {
    // Initialize auto token management if user is logged in
    const session = getSession();
    if (session && session.token) {
      logger.log('🚀 Initializing auto token management...');
      AutoTokenManager.startAutoRefresh();
    } else {
      // Even without backend session, try to setup Google token
      logger.log('🔑 Checking for Google tokens...');
      AutoTokenManager.autoSetupGoogleToken();
    }

    // Persist trial tokens found anywhere in the URL so visiting any page with
    // ?trialToken=... (eg. campaign root link) will survive navigation to Login.
    try {
      const qs = new URLSearchParams(window.location.search || '');
      const token = qs.get('trialToken') || qs.get('trialtoken') || qs.get('token');
      const sig = qs.get('sig') || qs.get('signature') || null;
      const locationId = qs.get('locationId') || qs.get('location') || null;
      const source = qs.get('source') || qs.get('utm_source') || null;
      if (token) {
        const invite = { token, sig, locationId, source, ts: Date.now() };
        const inviteStr = JSON.stringify(invite);
        try { sessionStorage.setItem('trial_invite', inviteStr); } catch (_) { }
        try { localStorage.setItem('trial_invite', inviteStr); } catch (_) { }
        try { window.dispatchEvent(new Event('trialInviteSet')); } catch (_) { }
        console.log('[App] persisted trial_invite from URL', invite);
      }
    } catch (e) {
      console.warn('[App] failed to persist trial token from URL', e && e.message);
    }

    // Cleanup on unmount
    return () => {
      AutoTokenManager.stopAutoRefresh();
    };
  }, []);

  return (
    <AppProvider>
      <AdminRoleProvider>
        <AdminDataProvider>
          <AccessGateProvider>
            <DefaultProfileProvider>
              <ProfilePlanProvider>
                <PermissionProvider>
                  <Router>
                    <TutorialWrapper>
                      <SessionExpiredHandler />
                      <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<VisibeenLandingPage />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/auth/callback" element={<OAuthCallback />} />
                        <Route path="/onboarding" element={<NewUserOnboarding />} />
                        <Route path="/connect-google" element={<GoogleConnect />} />
                        <Route path="/google-connect" element={<GoogleConnect />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/pricing" element={<PlansPricing />} />
                        <Route path="/about-us" element={<AboutUsPage />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                        <Route path="/disclaimer" element={<DisclaimerPage />} />
                        <Route path="/terms-of-service" element={<TermsOfService />} />
                        <Route path="/account-not-found" element={<AccountNotFound />} />
                        <Route path="/contact-us" element={<ContactUs />} />
                        <Route path="/create-account" element={<CreateAccount />} />
                        <Route path="/trial-landing" element={<TrialLanding />} />
                        <Route path="/trial/redeem/:token" element={<TrialRedeemRedirect />} />
                        <Route path="/live-demo/:templateId" element={<LiveDemo />} />
                        <Route path="/v/:slug" element={<VisitRedirect />} />
                        <Route path="/ads-page" element={<AdsPage />} />
                        <Route path="/ads" element={<AdsPage />} />

                        {/* Professional business URL without prefix: /state/city/slug */}
                        <Route path=":stateSlug/:citySlug/:slug" element={<BusinessView />} />

                        {/* Development/Testing Route - Remove in production */}
                        <Route path="/test-session-expired" element={<TestSessionExpired />} />

                        {/* Protected Routes */}
                        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                        <Route path="/profile/:locationId" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                        <Route path="/business-profile" element={<ProtectedRoute><BusinessProfile /></ProtectedRoute>} />
                        <Route path="/business-profile/:id" element={<ProtectedRoute><BusinessProfile /></ProtectedRoute>} />
                        <Route path="/client-onboarding" element={<ProtectedRoute><ClientOnboardingGuide /></ProtectedRoute>} />
                        <Route path="/get-edms/*" element={<ProtectedRoute><EDMsFlow /></ProtectedRoute>} />
                        <Route path="/create-post" element={<ProtectedRoute><CreatePostPage /></ProtectedRoute>} />
                        <Route path="/reputation" element={<ProtectedRoute><Reputation /></ProtectedRoute>} />
                        <Route path="/font-style" element={<ProtectedRoute><FontStyleSelection /></ProtectedRoute>} />
                        <Route path="/refer-earn" element={<ProtectedRoute><ReferEarn /></ProtectedRoute>} />
                        <Route path="/my-account/*" element={<ProtectedRoute><MyAccount /></ProtectedRoute>} />
                        <Route path="/my-account/account-information" element={<ProtectedRoute><DashboardLayout><AccountInfoForm /></DashboardLayout></ProtectedRoute>} />
                        <Route path="/my-account/gst-information" element={<ProtectedRoute><DashboardLayout><GSTInfoForm /></DashboardLayout></ProtectedRoute>} />
                        <Route path="/my-account/cro-information" element={<ProtectedRoute><DashboardLayout><CROInfoForm /></DashboardLayout></ProtectedRoute>} />
                        <Route path="/gmb-data" element={<ProtectedRoute><GMBDataFetcher /></ProtectedRoute>} />
                        <Route path="/free-website" element={<ProtectedRoute><FreeWebsite /></ProtectedRoute>} />
                        <Route path="/profile-strength-analysis" element={<ProtectedRoute><ProfileStrengthAnalysis /></ProtectedRoute>} />
                        <Route path="/profile-strength" element={<ProtectedRoute><ProfileStrengthPage /></ProtectedRoute>} />
                        <Route path="/profile-strength-results" element={<ProtectedRoute><ProfileStrengthResults /></ProtectedRoute>} />
                        <Route path="/heatmap-results" element={<ProtectedRoute><HeatmapResultsPage /></ProtectedRoute>} />
                        <Route path="/holidays" element={<ProtectedRoute><Holidays /></ProtectedRoute>} />
                        <Route path="/src/heatmap/heatmap-pages" element={<ProtectedRoute><HeatmapPage /></ProtectedRoute>} />
                        <Route path="/your-data" element={<ProtectedRoute><YourData /></ProtectedRoute>} />
                        <Route path="/vision-post-lab" element={<ProtectedRoute><VisionPostGeneratorPage /></ProtectedRoute>} />
                        <Route path="/post-generation-results" element={<ProtectedRoute><PostGenerationResults /></ProtectedRoute>} />
                        <Route path="/set-auto-reply" element={<ProtectedRoute><SetAutoReply /></ProtectedRoute>} />

                        {/* Protected Routes with DashboardLayout */}
                        <Route path="/reviews-management" element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <ReviewsManagement />
                            </DashboardLayout>
                          </ProtectedRoute>
                        } />
                        <Route path="/performance" element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <Performance />
                            </DashboardLayout>
                          </ProtectedRoute>
                        } />
                        <Route path="/monthly-plan" element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <MonthlyPlan />
                            </DashboardLayout>
                          </ProtectedRoute>
                        } />
                        <Route path="/task-management" element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <TaskManagementPage />
                            </DashboardLayout>
                          </ProtectedRoute>
                        } />
                        <Route path="/requests" element={
                          <ProtectedRoute>
                            <RequestsPage />
                          </ProtectedRoute>
                        } />

                        {/* Admin Dashboard Routes */}
                        <Route path="/admin-dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/profile" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/change-password" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/user-view/:id" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/employee-view/:id" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/employee-edit/:id" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/add-employee" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/payments" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/gmb-manager" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/gmbs" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin/gmbs" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/engagement" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/gmb-requests" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/EmployeeView/:id" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/client/:clientId" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/holidays" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/add-holiday" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/local-citation" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/add-lead" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/lead/:leadId" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/edit-lead/:leadId" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/meeting/:meetingId" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/attendance" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/tasks" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/photos" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/payment-profile/:paymentId" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/payment-profile" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/payment-edit/:paymentId" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/edit-client/:clientId" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/users" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/leads" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/meetings" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/clients" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/employee" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/employees" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />

                        {/* Festivals routes */}
                        <Route path="/admin-dashboard/festivals" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/add-festival" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/edit-festival/:id" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />

                        {/* Followups routes */}
                        <Route path="/admin-dashboard/followups" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/add-followup" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/followup/:followupId" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />

                        {/* Meeting routes */}
                        <Route path="/admin-dashboard/add-meeting" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/meeting/edit/:meetingId" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />

                        {/* Other admin routes */}
                        <Route path="/admin-dashboard/add-client" element={<AdminDashboard />} />
                        <Route path="/admin-dashboard/permissions" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin-dashboard/client-scores" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />

                        {/* Catch-all route - must be last */}
                        <Route path="*" element={<div>Page Not Found</div>} />
                      </Routes>
                      <UpgradePromptModal />
                    </TutorialWrapper>
                  </Router>
                </PermissionProvider>
              </ProfilePlanProvider>
            </DefaultProfileProvider>
          </AccessGateProvider>
        </AdminDataProvider>
      </AdminRoleProvider>
    </AppProvider>
  );
}

export default App;
